// Single generic credential-mismatch message (#1306). Every "wrong email" and
// "wrong password" outcome maps here so an attacker cannot tell a registered
// email from an unregistered one — i.e. cannot enumerate accounts. This is the
// FE half; the BFF/Firebase email-enumeration protection is the server half.
const INVALID_CREDENTIALS_MESSAGE = "Invalid email or password.";

// Generic, non-enumerating sign-up failure. Must NOT confirm that the email is
// already registered (which would also leak account existence on the sign-up
// surface).
const SIGNUP_FAILED_MESSAGE =
  "Couldn't create that account. Try signing in instead, or recover your password.";

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  "auth/account-changed":
    "Your signed-in account changed. Refresh this page before trying again.",
  "auth/account-name-invalid": "Enter a name for your personal account.",
  "auth/account-name-unconfirmed":
    "We couldn't confirm the account-name change. Try saving the same name again.",
  "auth/operation-not-allowed":
    "Email/password confirmation is unavailable for this account. Google-only account names can't be edited here yet.",
  // Sign-up: do not disclose that the email already exists.
  "auth/email-already-in-use": SIGNUP_FAILED_MESSAGE,
  // Format validation only — does not reveal whether an account exists.
  "auth/invalid-email": "That doesn't look like a valid email address.",
  "auth/weak-password": "Password is too short. Use at least 8 characters.",
  "auth/expired-action-code":
    "This password reset link has expired. Request a new one from the sign-in page.",
  "auth/invalid-action-code":
    "This password reset link is invalid or has already been used. Request a new one from the sign-in page.",
  // Credential-mismatch codes — ALL collapse to one generic message (#1306).
  // Unknown email and wrong password are indistinguishable to the attacker.
  "auth/user-not-found": INVALID_CREDENTIALS_MESSAGE,
  "auth/wrong-password": INVALID_CREDENTIALS_MESSAGE,
  "auth/invalid-credential": INVALID_CREDENTIALS_MESSAGE,
  "auth/invalid-login-credentials": INVALID_CREDENTIALS_MESSAGE,
  // Genuinely-distinct, non-enumerating cases stay distinct.
  "auth/too-many-requests": "Too many attempts. Try again in a few minutes.",
  "auth/network-request-failed": "Network error. Check your connection.",
  "auth/no-current-user": "Sign in to request another verification email.",
  "email-verification/unavailable":
    "We couldn't send the verification email. Try again in a moment.",
  "auth/user-disabled": "This account is disabled. Contact support.",
  "auth/unauthorized-domain":
    "Configuration error. Try refreshing. If it persists, contact support.",
  "auth/argument-error":
    "Configuration error. Try refreshing. If it persists, contact support.",
  "auth/google-sign-in-disabled":
    "Google sign-in is unavailable in preview environments. Use staging to sign in.",
  "auth/operation-cancelled": "Sign-in was cancelled.",
  // Legacy REST identity-toolkit codes — same collapse as their auth/* twins.
  EMAIL_NOT_FOUND: INVALID_CREDENTIALS_MESSAGE,
  INVALID_PASSWORD: INVALID_CREDENTIALS_MESSAGE,
};

const DEFAULT_AUTH_ERROR_MESSAGE =
  "Sign in failed. Please try again or contact support.";
const AUTH_CODE_PATTERN = /\b(auth\/[a-z0-9-]+)\b/;

export function getAuthErrorMessage(
  error: unknown,
  fallback = DEFAULT_AUTH_ERROR_MESSAGE,
): string {
  const code = getErrorCode(error);

  if (code && AUTH_ERROR_MESSAGES[code]) {
    return AUTH_ERROR_MESSAGES[code];
  }

  return fallback;
}

function getErrorCode(error: unknown): string | undefined {
  if (typeof error === "string") {
    return parseAuthCode(error) ?? error;
  }

  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") {
      return code;
    }
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? parseAuthCode(message) : undefined;
  }

  return undefined;
}

function parseAuthCode(message: string): string | undefined {
  return message.match(AUTH_CODE_PATTERN)?.[1];
}
